<template>
  <div id="app" class="app">
    <nav-bar />
    <div class="main-div">
      <router-view />
    </div>
    <footer-bar />
    <floating-robot-link
      :visible="hasAiRobotLink"
      @click="openAiRobotLink"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import menu from "@/menu.js";
import NavBar from "@/components/NavBar.vue";
import FloatingRobotLink from "@/components/FloatingRobotLink.vue";
// import AsideMenu from '@/components/AsideMenu.vue'
import FooterBar from "@/components/FooterBar.vue";
import { getSysConfigSetting } from "@/api/sys-config";
import { getLoginNotify } from "@/api/user";
import { setUserName } from '@/utils/auth'

export default defineComponent({
  name: "AppComponent",
  components: {
    FooterBar,
    FloatingRobotLink,
    NavBar,
  },
  data() {
    return {
      menu,
      aiRobotLink: "",
      loginNotifyTimer: null,
      loginNotifyChecking: false,
    };
  },
  computed: {
    hasAiRobotLink() {
      return !!this.aiRobotLink;
    },
    userToken() {
      return this.$store.getters.getUserToken;
    },
  },
  watch: {
    userToken: {
      immediate: true,
      handler(token) {
        if (token) {
          this.startLoginNotifyCheck();
        } else {
          this.stopLoginNotifyCheck();
        }
      },
    },
  },
  created() {
    this.loadAiRobotLink();
    console.log(this.$store.state);
  },
  beforeDestroy() {
    this.stopLoginNotifyCheck();
  },
  methods: {
    async loadAiRobotLink() {
      try {
        const res = await getSysConfigSetting("ai_rebot_link");
        const settingValue = this.extractSettingValue(res);
        this.aiRobotLink = this.normalizeLink(settingValue);
      } catch (error) {
        console.error("获取 AI 机器人链接失败", error);
      }
    },
    extractSettingValue(res) {
      const data = res && res.data;
      if (!data) {
        return "";
      }
      if (typeof data === "string") {
        return data;
      }
      if (typeof data.ai_rebot_link === "string") {
        return data.ai_rebot_link;
      }
      if (typeof data.value === "string") {
        return data.value;
      }
      if (Array.isArray(data)) {
        const matchedItem = data.find((item) => {
          return (
            item &&
            (item.key === "ai_rebot_link" ||
              item.keys === "ai_rebot_link" ||
              item.configKey === "ai_rebot_link")
          );
        });
        if (!matchedItem) {
          return "";
        }
        return (
          matchedItem.value ||
          matchedItem.configValue ||
          matchedItem.settingValue ||
          ""
        );
      }
      if (typeof data === "object") {
        const firstValue = Object.values(data)[0];
        if (typeof firstValue === "string") {
          return firstValue;
        }
      }
      return "";
    },
    normalizeLink(link) {
      if (!link || typeof link !== "string") {
        return "";
      }
      const trimmedLink = link.trim();
      if (!trimmedLink) {
        return "";
      }
      if (/^https?:\/\//i.test(trimmedLink)) {
        return trimmedLink;
      }
      return `https://${trimmedLink}`;
    },
    openAiRobotLink() {
      if (!this.aiRobotLink) {
        return;
      }
      window.open(this.aiRobotLink, "_blank", "noopener,noreferrer");
    },
    startLoginNotifyCheck() {
      if (this.loginNotifyTimer) {
        return;
      }
      this.loginNotifyTimer = setInterval(() => {
        this.checkLoginNotify();
      }, 10000);
      this.checkLoginNotify();
    },
    stopLoginNotifyCheck() {
      if (this.loginNotifyTimer) {
        clearInterval(this.loginNotifyTimer);
        this.loginNotifyTimer = null;
      }
    },
    checkLoginNotify() {
      if (this.loginNotifyChecking || !this.userToken) {
        return;
      }
      this.loginNotifyChecking = true;
      getLoginNotify()
        .then((res) => {
          if (res && res.data && res.data.type === "login_notify") {
            this.showLoginNotifyDialog(res.data);
          }
          if (res && res.data && !res.data.type && res.data.userInfo) {
            let username = res.data.userInfo.username.String ? res.data.userInfo.username.String : "";
            if (res.data.userInfo.nickName) {
              username = res.data.userInfo.nickName
            }
            if (!username) {
              username = res.data.userInfo.usrPhone || (res.data.userInfo.email.String ? res.data.userInfo.email.String : '')
            }
            this.$store.commit('SET_USER_NAME', username)
            setUserName(username)
          }
        })
        .catch((err) => {
          console.error("检查登录通知失败", err);
        })
        .finally(() => {
          this.loginNotifyChecking = false;
        });
    },
    showLoginNotifyDialog(msg) {
      const loginTime = this.formatTimestamp(msg.loginTime);
      const content = `您的账号于 ${loginTime} 在 ${msg.loginLocation || "新设备"} 登录<br>登录IP：${msg.loginIp || "未知"}<br>设备：${msg.deviceName || "未知设备"}<br><br>如非本人操作，请及时修改密码`;
      this.$buefy.dialog.alert({
        title: msg.title || "账号异地登录提醒",
        message: content,
        type: "is-warning",
        confirmText: "知道了",
        hasIcon: true,
        canCancel: ["x", "escape", "outside"],
      });
    },
    formatTimestamp(timestamp) {
      if (!timestamp) {
        return "未知时间";
      }
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      const second = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
  },
});
</script>
<style lang="scss">
.app {
  margin: 0 auto;
  max-width: 1920px;
}

@media (max-width: 1200px) {
  html {
    width: 100%;
    height: 100%;
    overflow-x: auto;
  }
}

.main-div {
  box-sizing: border-box;
}

.modal {
  z-index: 10000 !important;
}

.no-data {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  img {
    width: 300px;
    height: 300px;
  }

  .no-data-text {
    font-size: 16px;
    color: #999;
    text-align: center;
  }
}

//@font-face {
//  font-family: 'SourceHanSansSC';
//  src: url('@/assets/fonts/SourceHanSansSC-Medium.otf') format('opentype');
//  font-weight: normal;
//  font-style: normal;
//}
//
//@font-face {
//  font-family: 'SourceHanSansSCNomal';
//  src: url('@/assets/fonts/SourceHanSansSC-Normal.otf') format('opentype');
//  font-weight: normal;
//  font-style: normal;
//}
</style>
